import { Copy, Languages, Loader2, Wand2 } from 'lucide-react'
import type { FormEvent } from 'react'
import type { TrackTitleLanguage, TrackTitleSuggestion } from '../../../api/suno'
import { Button, IconButton, Pill } from '../../../components/ui'
import styles from '../../../pages/ToolsPage.module.css'
import { languageOptions } from './useTrackTitleGeneratorData'

type TrackTitleWorkspaceProps = {
  artist: string
  error: string | null
  language: TrackTitleLanguage
  languageMenuOpen: boolean
  loading: boolean
  onArtistChange: (value: string) => void
  onCopySuggestion: (suggestion: TrackTitleSuggestion) => void
  onLanguageMenuToggle: () => void
  onSelectLanguage: (language: TrackTitleLanguage) => void
  onStylePromptChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  promptValid: boolean
  selectedLanguageLabel: string
  stylePrompt: string
  suggestions: TrackTitleSuggestion[]
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

export const TrackTitleWorkspace = ({
  artist,
  error,
  language,
  languageMenuOpen,
  loading,
  onArtistChange,
  onCopySuggestion,
  onLanguageMenuToggle,
  onSelectLanguage,
  onStylePromptChange,
  onSubmit,
  promptValid,
  selectedLanguageLabel,
  stylePrompt,
  suggestions
}: TrackTitleWorkspaceProps) => (
  <section className={cx(styles['tools-layout'], styles['tools-detail-layout'])} aria-label="Creative tool workspace">
    <form className={styles['tools-form-panel']} onSubmit={(event) => void onSubmit(event)}>
      <div className={styles['tools-panel-heading']}>
        <span className={styles['tools-panel-icon']}>
          <Wand2 size={18} />
        </span>
        <div>
          <h2>Naming Prompt</h2>
          <p>Generate 12 release-ready options from a Suno style prompt.</p>
        </div>
        <div className={styles['tools-language-popover']}>
          <IconButton
            aria-expanded={languageMenuOpen}
            className={styles['tools-language-trigger']}
            icon={<Languages size={16} />}
            label={`Output language: ${selectedLanguageLabel}`}
            onClick={onLanguageMenuToggle}
            title={`Output language: ${selectedLanguageLabel}`}
          />
          {languageMenuOpen && (
            <div className={styles['tools-language-menu']} role="dialog" aria-label="Output language">
              {languageOptions.map((option) => (
                <button
                  aria-pressed={language === option.value}
                  className={cx(styles['tools-language-menu-item'], language === option.value && 'active')}
                  key={option.value}
                  onClick={() => onSelectLanguage(option.value)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <label className="form-label" htmlFor="track-name-style-prompt">
        Style prompt
      </label>
      <textarea
        className={cx('form-input', styles['tools-style-prompt'])}
        id="track-name-style-prompt"
        maxLength={3000}
        onChange={(event) => onStylePromptChange(event.target.value)}
        placeholder="Atmospheric liquid drum and bass, warm pads, female vocal chops, night drive, glossy futuristic mix..."
        rows={9}
        value={stylePrompt}
      />

      <label className="form-label" htmlFor="track-name-artist">
        Artist (optional)
      </label>
      <input
        className="form-input"
        id="track-name-artist"
        maxLength={120}
        onChange={(event) => onArtistChange(event.target.value)}
        placeholder="Leave empty to generate Artist - Title"
        value={artist}
      />

      <div className={styles['tools-language-control']} aria-label="Output language">
        {languageOptions.map((option) => (
          <button
            className={cx(styles['tools-language-button'], language === option.value && 'active')}
            key={option.value}
            onClick={() => onSelectLanguage(option.value)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && <div className={styles['tools-error']}>{error}</div>}

      <Button className={cx(styles['tools-generate-button'])} disabled={!promptValid || loading} type="submit" variant="primary">
        {loading ? <Loader2 className="tools-spin" size={16} /> : <Wand2 size={16} />}
        {loading ? 'Generating...' : 'Generate names'}
      </Button>
    </form>

    <section className={styles['tools-results-panel']} aria-label="Generated track names">
      <div className={styles['tools-results-heading']}>
        <div>
          <h2>Suggestions</h2>
          <p>Artist - Title</p>
        </div>
        <Pill strong>{suggestions.length}/12</Pill>
      </div>

      {loading && (
        <div className={styles['tools-results-state']}>
          <Loader2 className="tools-spin" size={24} />
          <span>Generating name ideas...</span>
        </div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className={styles['tools-results-state']}>
          <Wand2 size={24} />
          <span>Enter a style prompt to generate 12 options.</span>
        </div>
      )}

      {!loading && suggestions.length > 0 && (
        <ol className={styles['tools-suggestion-list']}>
          {suggestions.map((suggestion, index) => (
            <li className={styles['tools-suggestion-item']} key={`${suggestion.text}-${index}`}>
              <span>{suggestion.text}</span>
              <IconButton
                icon={<Copy size={15} />}
                label={`Copy ${suggestion.text}`}
                onClick={() => onCopySuggestion(suggestion)}
                title="Copy"
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  </section>
)
